import React from 'react';
import ActionMenu, { ActionMenuItem } from '@components/RingilComponents/ActionMenu.tsx';
import SvgShuffle from '@icons/shuffle.svg?react';
import SvgArrowRightDown from '@icons/arrowDown.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ExpeditionLocality } from '@app/pages/expedition/create/types.ts';
import { loadingCandidateIndexes } from '@app/pages/expedition/create/routeRules.ts';

interface ComponentProps {
  orderId: string;
  locIndex: number;
  locations: ExpeditionLocality[];
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  /** Shows the trigger as a labelled button rather than a bare icon. */
  label?: string;
  dataTestId?: string;
}

/**
 * Moves one order's loading to a different stop. Sits in the order's header, so it acts on the
 * order it is shown with.
 *
 * `ActionMenu` renders nothing when the list is empty, so an order with nowhere else to go shows no
 * control at all.
 */
const MoveOrderLoadingButton: React.FC<ComponentProps> = ({
                                                            orderId,
                                                            locIndex,
                                                            locations,
                                                            onMoveOrderLoading,
                                                            label,
                                                            dataTestId,
                                                          }) => {
  const { tr } = useTranslationLgs();

  const items: ActionMenuItem[] = loadingCandidateIndexes(locations, locIndex).map(idx => ({
      key: String(idx),
      label:
        locations[idx].location?.locationName ||
        locations[idx].manualAddress?.name ||
        tr('CreateExpedition.moveOrderToLocationLabelForUnNamed', 'Stop {{stop}}', { stop: idx + 1 }),
      icon: <SvgArrowRightDown />,
      onClick: () => onMoveOrderLoading(orderId, idx),
      dataTestId: dataTestId ? `${dataTestId}-stop${idx + 1}` : undefined,
  }));

  return (
    <ActionMenu
      icon={<SvgShuffle className={'size-4 shrink-0'} />}
      label={label}
      mode={label ? 'outlined-rectangle' : 'table'}
      tooltip={label ? undefined : tr('CreateExpedition.loadInAnotherLocation', 'Load in another location')}
      heading={tr('CreateExpedition.moveOrderLoadingToLocation', 'Load order at another location')}
      items={items}
      dataTestId={dataTestId}
    />
  );
};

export default MoveOrderLoadingButton;